import { Component, OnInit, OnDestroy, Input, Injector, ViewChild, ElementRef, Output, EventEmitter } from '@angular/core';

import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import { FilterService } from '../../services/abstract-filter.service';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { FilterTypeModel } from '../../models/abstract-filter-type.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { AutocompleteComponent } from '../../../Forms/components/Autocomplete/autocomplete.component';
import { AppearCondition, AutocompleteCustomOption } from '../../../Forms/components/Autocomplete/autocomplete-custom-option.model';
import { FilterQuickOptionModel } from '../../models/filter-quick-option.model';
import { ChipModel } from '../../../Forms/models/chip.model';
import { ReachFilterModel } from '../../../TargetingShared/model/reach-filter.model';
import { AgprComponentStyleEnum } from '../../../Basic/enums/social-analysis-types.enum';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { FilterWidgetActionTypeEnum, FilterWidgetCloseAction, FilterWidgetComponent } from '../FilterWidget/filter-widget.component';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';

/**
 * ### Accessibility Usage
 * + ***ariaDescribedBy*** - use this if the panel has an external error message, or it needs extra description for screen reader. Use message's id
 * + ***ariaLabel*** - use this to provide an accessible label for the autocomplete component. Don't use this if ariaLabelledBy is used
 * + ***ariaLabelledBy*** - use this to associate an external label with the autocomplete. Use id of the label as this value. This overwrites ariaLabel
 * + ***id*** - provide an unique id so that this component can be correctly recognized by screen readers. Use interpolation [id]="'unique_id'"
 */
@Component({
    selector: 'agpr-filter-dropdown',
    templateUrl: './filter-dropdown.component.html',
    styleUrls: ['./filter-dropdown.component.scss'],
    standalone: false
})
export class FilterDropdownComponent implements OnInit, OnDestroy {
    readonly agprComponentStyle = AgprComponentStyleEnum;
    readonly buttonTypes = ButtonTypesEnum;

    subscriptions: Subscription[] = [];
    overlayContainer: Element;

    quickFilterQuery: string;
    quickSuggestions: (query: string) => Observable<AutocompleteCustomOption[]>;
    filters: FilterTypeModel[];
    hasFilters: boolean = false;

    @Input() id: string;
    @Input() disabled: boolean = false;
    @Input() tooltip: string = 'Core.button.filter';
    @Input() showQuickFilter: boolean = false;
    @Input() showSelectedFilters: boolean = false;
    @Input() quickFilterPlaceholder: string = '';
    @Input() quickOptionsPerType: number = 3;
    @Input() minLength: number = 3;
    @Input() style: AgprComponentStyleEnum = AgprComponentStyleEnum.social;
    @Input() ariaLabel: string = null;
    @Input() ariaLabelledBy: string = '';
    @Input() ariaDescribedBy: string = '';
    @Input() allowMultipleKeywordsInput: boolean = true;
    @Input() inputGroupRight: boolean = false;

    // briefings 'rules' overrides this with 'fa-regular fa-network-wired'
    @Input() iconClass: string = 'fa-regular fa-duotone fa-filter';

    @Output() filtersApplied: EventEmitter<void> = new EventEmitter<void>();

    @ViewChild('autocomplete') autocomplete: AutocompleteComponent;
    @ViewChild('filterButton') filterButton: ElementRef;

    constructor(
        protected injector: Injector,
        protected coreDialogService: CoreDialogService,
        protected filterService: FilterService,
        protected localizationService: LocalizationService,
        protected ref: ElementRef
    ) {
        this.quickSuggestions = this.suggestQuickOptions.bind(this);
    }

    ngOnInit(): void {
        this.overlayContainer = document.getElementById('main-content');

        this.subscriptions.push(
            this.filterService.getFlatFilters().subscribe(
                (filters) => {
                    this.filters = filters;
                    this.filters.forEach(filter => {
                        if (filter instanceof ReachFilterModel) {
                            if (!filter.hasValueSet()) {
                                filter.currentChips = [];
                            }
                        }
                    });
                    this.hasFilters = this.showSelectedFilters && !filters.every(filter => !filter.chips.length);
                }
            )
        );
    }
    ngOnDestroy(): void {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
        this.subscriptions = [];
    }

    open(): void {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer
            },
            outputs: {
                dialogClose: (closeAction: FilterWidgetCloseAction) => {
                    if (closeAction?.action === FilterWidgetActionTypeEnum.APPLY) {
                        this.filtersApplied.emit();
                    }
                }
            }
        };
        this.coreDialogService.open(FilterWidgetComponent, io, this.injector);
        this.filterButton.nativeElement?.blur();
    }

    getQuickOption(option: FilterQuickOptionModel): AutocompleteCustomOption {
        const optionLabel = this.localizationService.transform(option.label);
        const filterLabel = this.localizationService.transform(option.filter.label);

        return {
            labelHtml: `${optionLabel} - <span class="agpr-item-metadata">${filterLabel}</span>`,
            action: (searchString?: string) => {
                const queries = searchString?.split(/(")?,(?=(?:[^\"]*\"[^\"]*\")*[^\"]*$)\1 */g);
                if(this.allowMultipleKeywordsInput && queries.length > 1){
                    queries.forEach(query => {
                        if(query){
                            option.label = query;
                            option.value = query;
                            if(!option.filter.getValue()?.includes?.(query)){
                                option.filter.addQuickOption(option);
                            }
                        }
                    });
                } else {
                    if (option.filter.useExactQuickSearchString && searchString) {
                        option.value = searchString;
                    }
                    option.filter.addQuickOption(option);
                }
                this.quickFilterQuery = '';
                this.autocomplete.clearInput();
                this.filterService.applyFilters();
                this.filtersApplied.emit();
            },
            appearCondition: AppearCondition.always,
            clearOnSelect: true
        };
    }
    suggestQuickOptions(query: string): Observable<AutocompleteCustomOption[]> {
        const options = [];
        const requests = [];
        const translateMethod = this.localizationService.transform.bind(this.localizationService);

        this.filters.forEach(filter => {
            const quickFilter = filter.quickAutocomplete(query, translateMethod);
            if (Array.isArray(quickFilter)) {
                const filterOptions = quickFilter.slice(0, this.quickOptionsPerType).map(opt => this.getQuickOption(opt));
                options.push(
                    ...filterOptions
                );
            } else if ('subscribe' in quickFilter) {
                requests.push(
                    quickFilter.pipe(map(
                        opts => opts.map(opt => this.getQuickOption(opt))
                    ))
                );
            }
        });

        if (requests.length > 0) {
            return combineLatest([
                of(options),
                ...requests
            ]).pipe(
                map(responses => {
                    const allOptions = [];
                    responses.forEach(response => {
                        if (response) {
                            allOptions.push(...responses);
                        }
                    });
                    return allOptions;
                })
            );
        }
        return of(options);
    }

    removeChip(filter: FilterTypeModel, chip: ChipModel): void {
        filter.removeCurrentChip(chip);
        this.filterService.applyFilters();
        this.filtersApplied.emit();
    }
}
